import { Component } from "react";
import { logError } from "../api/logger";

/**
 * Top-level error boundary.
 *
 * Catches render-time errors anywhere in the tree and shows a recoverable
 * fallback instead of a blank white page. Kept as a class because React has
 * no hook equivalent of componentDidCatch.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Routed through the shared logger so a Sentry/RUM hook can pick it up
    // alongside async page-level failures (see api/logger.js).
    logError("UI crashed", error, info);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      // data-error-boundary lets scripts/prerender.mjs refuse to bake this
      // fallback into a static snapshot.
      <div
        data-error-boundary
        className="min-h-screen bg-gray-50 flex items-center justify-center px-4 dark:bg-slate-950"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center dark:bg-slate-900 dark:border-slate-800">
          <div className="text-5xl mb-4">😿</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2 dark:text-gray-100">
            Что-то пошло не так
          </h1>
          <p className="text-gray-500 text-sm mb-6 dark:text-gray-400">
            Интерфейс столкнулся с непредвиденной ошибкой. Попробуйте обновить страницу.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition-colors dark:border-slate-700 dark:bg-slate-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Попробовать снова
            </button>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 text-sm text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors"
            >
              Обновить
            </button>
          </div>
        </div>
      </div>
    );
  }
}
